import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import 'leaflet/dist/leaflet.css';
import { useTasks } from './Tasks/hooks/useTasks';
import TaskListMap from './Tasks/components/Maps/TaskListMap';
import { getTaskStatusColor } from './Tasks/utils/taskUtils';
import DonutChart from '../components/charts/DonutChart';
// import MultiLineChart from '../components/charts/MultiLineChart'; // (removed daily activity card per request)
import { FiClock, FiAlertTriangle, FiFileText, FiCheckCircle } from 'react-icons/fi';

const Dashboard = () => {
  const { isAuthenticated, company } = useAuth();
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate, label: 'This Month' };
  });
  const navigate = useNavigate();
  const { tasks, loading: tasksLoading, error: tasksError, fetchTasks } = useTasks();
  const [quotes, setQuotes] = useState([]);
  const [quoteSummary, setQuoteSummary] = useState({ total: 0, byStatus: {}, totalAmount: 0, convertedAmount: 0 });
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [companyStatsError, setCompanyStatsError] = useState(null);
  const [companyStatsLoading, setCompanyStatsLoading] = useState(false);
  const [bizStats, setBizStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [sourceBreakdown, setSourceBreakdown] = useState({ total: 0, sources: [] });
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);

  // ─── Matte palette (matching Accounts/Operations dashboards) ────────────────
  const matte = useMemo(() => ({
    blue: '#74a0d6',
    purple: '#8a7bb8',
    green: '#79b38a',
    red: '#c57878',
    amber: '#e39a2b',
    teal: '#55a39b',
    rose: '#b77a98',
    indigo: '#7a76b5',
    allGrey: '#9ca3af',
    rust: '#b3784f'
  }), []);

  const toDateStr = (val) => {
    if (!val) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return undefined;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return String(val).slice(0, 10);
    }
  };

  const Card = ({ title, subtitle, actions, children }) => (
    <div style={{
      borderRadius: 12,
      padding: 12,
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 10px 25px rgba(0,0,0,0.12)'
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:12 }}>
        <div>
          <div style={{ fontWeight: 500, color:'#fff', fontSize: 13, textAlign:'left' }}>{title}</div>
          {subtitle ? <div style={{ color:'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2, textAlign:'left' }}>{subtitle}</div> : null}
        </div>
        {actions ? <div style={{ display:'flex', gap:8, alignItems:'center' }}>{actions}</div> : null}
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );

  const StatItem = ({ icon, label, value, color, onClick, subtitle, loading }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        padding: '10px 12px',
        borderRadius: 10,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        textAlign: 'left',
        opacity: loading ? 0.85 : 1
      }}
      title={subtitle}
    >
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        flexShrink: 0
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
          {loading ? '…' : (value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value))) ? '-' : value}
        </div>
      </div>
    </button>
  );

  const SegBar = ({ segments }) => {
    const totalSeg = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={{ display:'flex', height: 10, borderRadius: 5, overflow:'hidden', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.08)' }}>
        {segments.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={s.onClick}
            title={s.title}
            style={{
              width: `${(Number(s.value || 0) / totalSeg) * 100}%`,
              background: s.color,
              border: 'none',
              padding: 0,
              cursor: s.onClick ? 'pointer' : 'default',
              opacity: 0.9,
              borderRadius: 0,
              borderTopLeftRadius: i === 0 ? 5 : 0,
              borderBottomLeftRadius: i === 0 ? 5 : 0,
              borderTopRightRadius: i === segments.length - 1 ? 5 : 0,
              borderBottomRightRadius: i === segments.length - 1 ? 5 : 0
            }}
          />
        ))}
      </div>
    );
  };

  const Legend = ({ items }) => (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop: 10 }}>
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={it.onClick}
          disabled={!it.onClick}
          style={{
            display:'flex',
            justifyContent:'space-between',
            alignItems:'center',
            gap:10,
            width:'100%',
            background:'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.10)',
            color:'#fff',
            padding:'8px 10px',
            borderRadius: 12,
            cursor: it.onClick ? 'pointer' : 'default',
            textAlign: 'left'
          }}
          title={it.title}
        >
          <span style={{ display:'inline-flex', alignItems:'center', gap:8, color:'rgba(255,255,255,0.9)', fontSize: 12 }}>
            <span style={{ width:10, height:10, borderRadius: 3, background: it.color }} />
            {it.label}
          </span>
          <span style={{ fontWeight: 600, color: it.valueColor || '#fff' }}>{it.value}</span>
        </button>
      ))}
    </div>
  );

  // Fetch tasks for selected range
  useEffect(() => {
    if (!isAuthenticated || !company?.id) return;
    fetchTasks({
      startDate: toDateStr(selectedDateRange?.startDate),
      endDate: toDateStr(selectedDateRange?.endDate)
    });
  }, [isAuthenticated, company?.id, selectedDateRange?.startDate, selectedDateRange?.endDate, fetchTasks]);

  // Fetch company-wide stats (aligns with OperationsDashboard overdue count)
  useEffect(() => {
    const run = async () => {
      try {
        setCompanyStatsError(null);
        setCompanyStatsLoading(true);
        const { data } = await api.get('/api/company/stats');
        setCompanyStats(data?.data || data || null);
      } catch (e) {
        setCompanyStats(null);
        setCompanyStatsError('Failed to load company stats');
      } finally {
        setCompanyStatsLoading(false);
      }
    };
    if (isAuthenticated) run();
  }, [isAuthenticated, company?.id]);

  // Fetch mini quotes list for selected range (recent 5)
  useEffect(() => {
    const run = async () => {
      try {
        setQuotesError(null);
        setQuotesLoading(true);
        const params = {
          page: 1,
          pageSize: 200,
          fromDate: toDateStr(selectedDateRange?.startDate),
          toDate: toDateStr(selectedDateRange?.endDate)
        };
        const { data } = await api.get('/api/quotes', { params });
        const rows = Array.isArray(data?.data) ? data.data : [];
        setQuotes(rows);
        // Build summary
        const byStatus = {};
        let totalAmount = 0;
        let convertedAmount = 0;
        rows.forEach((q) => {
          const s = String(q.status || '').toUpperCase();
          byStatus[s] = (byStatus[s] || 0) + 1;
          totalAmount += Number(q.total || 0);
          if (s === 'CONVERTED') convertedAmount += Number(q.total || 0);
        });
        setQuoteSummary({
          total: Number(data?.total || rows.length || 0),
          byStatus,
          totalAmount,
          convertedAmount
        });
      } catch (e) {
        console.error('Failed to load quotes', e);
        setQuotes([]);
        setQuoteSummary({ total: 0, byStatus: {}, totalAmount: 0, convertedAmount: 0 });
        setQuotesError('Failed to load quotes');
      } finally {
        setQuotesLoading(false);
      }
    };
    if (isAuthenticated) run();
  }, [isAuthenticated, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  // Fetch Accounts-style sales stats for selected range
  useEffect(() => {
    const run = async () => {
      try {
        setSalesError(null);
        setSalesLoading(true);
        const fromParam = toDateStr(selectedDateRange?.startDate);
        const toParam = toDateStr(selectedDateRange?.endDate);
        const [statsRes, sourceRes, trendsRes] = await Promise.allSettled([
          api.get('/api/company/business-stats', { params: { fromDate: fromParam, toDate: toParam } }),
          api.get('/api/company/revenue-by-source', { params: { fromDate: fromParam, toDate: toParam } }),
          api.get('/api/company/business-trends', { params: { fromDate: fromParam, toDate: toParam } })
        ]);
        if (statsRes.status === 'fulfilled') setBizStats(statsRes.value?.data?.data || null);
        if (sourceRes.status === 'fulfilled') setSourceBreakdown(sourceRes.value?.data?.data || { total: 0, sources: [] });
        if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value?.data?.data || null);

        const anyRejected = [statsRes, sourceRes, trendsRes].some(r => r.status === 'rejected');
        if (anyRejected) setSalesError('Some sales stats could not be loaded');
      } catch (e) {
        setBizStats(null);
        setSourceBreakdown({ total: 0, sources: [] });
        setTrends(null);
        setSalesError('Failed to load sales stats');
      } finally {
        setSalesLoading(false);
      }
    };
    if (isAuthenticated && company?.id) run();
  }, [isAuthenticated, company?.id, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  // Helpers
  const isResolved = (status) => ['resolved', 'rejected', 'cancelled'].includes(String(status || '').toLowerCase());
  const isOpenStatus = (status) => ['incoming', 'pending', 'in-progress'].includes(String(status || '').toLowerCase());
  const isConverted = (q) => String(q?.status || '').toUpperCase() === 'CONVERTED';

  const formatINR = (val, digits = 0) => {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(val || 0));
    } catch {
      return val;
    }
  };

  // Compute KPI metrics from tasks and quotes
  const computeKpis = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let openTasks = 0;
    let dueToday = 0;
    let overdueLocal = 0;
    (tasks || []).forEach((t) => {
      const status = String(t.status || '').toLowerCase();
      const due = t?.dueDate ? new Date(t.dueDate) : null;
      if (isOpenStatus(status)) openTasks += 1;
      if (due) {
        if (due >= startOfToday && due <= endOfToday && !isResolved(status)) dueToday += 1;
        if (due < startOfToday && !isResolved(status)) overdueLocal += 1;
      }
    });
    const quotesCreated = quotes.length || 0;
    const quotesConverted = (quotes || []).filter(isConverted).length || 0;
    const conversionRate = quotesCreated ? Math.round((quotesConverted / quotesCreated) * 100) : 0;
    // Use backend overdueTasks if available to match OperationsDashboard; fallback to local calc
    const overdue = typeof companyStats?.overdueTasks === 'number' ? companyStats.overdueTasks : overdueLocal;
    return { openTasks, dueToday, overdue, quotesCreated, quotesConverted, conversionRate };
  };
  const kpis = computeKpis();

  // ─── Sales computations (AccountsDashboard parity) ─────────────────────────
  const amountReceived = Number(bizStats?.amountReceived || 0);
  const amountDue = Number(bizStats?.amountDue || 0);
  const totalBilling = Math.max(0, amountReceived + amountDue);
  const collectionPct = totalBilling ? Math.round((amountReceived / totalBilling) * 100) : 0;

  const quotesCreatedCount = Number(bizStats?.quotesCreatedCount || 0);
  const ordersReceivedCount = Number(bizStats?.ordersReceivedCount || 0);
  const invoicesPaidCount = Number(bizStats?.invoicesPaidCount || 0);
  const quoteToOrderPct = quotesCreatedCount ? Math.round((ordersReceivedCount / quotesCreatedCount) * 100) : null;
  const orderToPaidPct = ordersReceivedCount ? Math.round((invoicesPaidCount / ordersReceivedCount) * 100) : null;
  const avgInvoiceValue = invoicesPaidCount ? (amountReceived / invoicesPaidCount) : null;

  // POS stats (optional, if backend provides)
  const posReceived = Number(bizStats?.posAmountReceived || 0);
  const posSalesCount = Number(bizStats?.posSalesCount || 0);
  const posSharePct = amountReceived ? Math.round((posReceived / amountReceived) * 100) : null;
  const avgPosValue = posSalesCount ? (posReceived / posSalesCount) : null;

  // Source labels & colors
  const sourceLabels = useMemo(() => ({
    POS: 'POS',
    DIRECT: 'Direct',
    CONVERTED_QUOTE: 'Converted Quote',
    CUSTOMER_ORDER: 'Customer Order',
    QUOTE_ADVANCE: 'Quote Advance',
    RECURRING_PROFORMA: 'Recurring'
  }), []);

  const sourceColors = useMemo(() => ({
    POS: matte.blue,
    DIRECT: matte.teal,
    CONVERTED_QUOTE: matte.amber,
    CUSTOMER_ORDER: matte.rose,
    QUOTE_ADVANCE: matte.purple,
    RECURRING_PROFORMA: matte.rust
  }), [matte]);

  const allSources = useMemo(() => {
    const list = Array.isArray(sourceBreakdown?.sources) ? sourceBreakdown.sources : [];
    return (list || [])
      .map(s => ({
        source: String(s.source || '').toUpperCase(),
        label: sourceLabels[String(s.source || '').toUpperCase()] || String(s.source || '').replace(/_/g, ' '),
        amount: Number(s.amount || 0),
        count: Number(s.count || 0),
        color: sourceColors[String(s.source || '').toUpperCase()] || matte.indigo
      }))
      .filter(s => !!s.source)
      .sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }, [sourceBreakdown, sourceLabels, sourceColors, matte.indigo]);

  const revenuePerDay = useMemo(() => trends?.revenuePerDay || [], [trends]);
  // const quotesPerDay = useMemo(() => trends?.quotesPerDay || [], [trends]); // (removed daily activity card per request)
  // const ordersPerDay = useMemo(() => trends?.ordersPerDay || [], [trends]); // (removed daily activity card per request)
  const revenueBySourcePerDay = useMemo(() => trends?.revenueBySourcePerDay || [], [trends]);

  const revenueBySourceSeries = useMemo(() => {
    const bySrc = Object.fromEntries((revenueBySourcePerDay || []).map(r => [String(r.source || '').toUpperCase(), r.data || []]));
    const ordered = (allSources || []).map(s => String(s.source || '').toUpperCase()).filter(Boolean);
    const uniq = Array.from(new Set(ordered));
    return uniq
      .map((src) => {
        const data = bySrc[src] || [];
        const meta = (allSources || []).find(s => String(s.source || '').toUpperCase() === src);
        return {
          name: meta?.label || src,
          color: meta?.color || matte.indigo,
          data
        };
      })
      .filter(s => Array.isArray(s.data) && s.data.some(d => Number(d?.amount || 0) > 0))
      .slice(0, 4);
  }, [revenueBySourcePerDay, allSources, matte.indigo]);

  const computeStatusDistribution = () => {
    const map = { incoming:0, pending:0, 'in-progress':0, resolved:0, rejected:0, cancelled:0 };
    (tasks || []).forEach((t) => {
      const s = String(t.status || '').toLowerCase();
      if (map[s] !== undefined) map[s] += 1;
    });
    const entries = Object.entries(map);
    const maxV = Math.max(1, ...entries.map(([, v]) => v));
    return { entries, maxV };
  };
  const statusDist = computeStatusDistribution();

  // Activity feed combining tasks and quotes
  const activity = (() => {
    const items = [];
    (tasks || []).forEach((t) => {
      const ts = t?.createdAt ? new Date(t.createdAt) : (t?.dueDate ? new Date(t.dueDate) : null);
      if (!ts) return;
      items.push({
        type: 'task',
        ts,
        title: t.description || (t.customer?.name ? `Task for ${t.customer.name}` : `Task #${t.id}`),
        subtitle: t.customer?.name || '',
        status: t.status
      });
    });
    (quotes || []).forEach((q) => {
      const ts = q?.date ? new Date(q.date) : null;
      if (!ts) return;
      items.push({
        type: 'quote',
        ts,
        title: q.number || 'Quote',
        subtitle: q.customerSnapshot?.name || '',
        status: q.status,
        amount: q.total
      });
    });
    items.sort((a, b) => b.ts.getTime() - a.ts.getTime());
    return items.slice(0, 10);
  })();
  const [activityFilter, setActivityFilter] = useState('tasks'); // 'tasks' | 'quotes'
  const filteredActivity = activity.filter((a) => activityFilter === 'tasks' ? a.type === 'task' : a.type === 'quote');
  const [activityPage, setActivityPage] = useState(1);
  const activityPageSize = 5;
  const activityTotalPages = Math.max(1, Math.ceil(filteredActivity.length / activityPageSize));
  const activityPageItems = filteredActivity.slice((activityPage - 1) * activityPageSize, activityPage * activityPageSize);
  useEffect(() => { setActivityPage(1); }, [activityFilter, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  if (!isAuthenticated) {
    return (
      <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
        <div className="page-content" style={{ background: 'transparent' }}>
          <h2 style={{ fontSize: 18, margin: 0, color: '#fff' }}>Dashboard</h2>
          <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: 16 }}>Please log in to access the dashboard</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
      <style>{`
        .dark-date-filters .header-date-filters .drp-input {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-input:hover {
          background: rgba(255,255,255,0.12) !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        .dark-date-filters .header-date-filters .date-separator {
          color: rgba(255,255,255,0.5) !important;
        }
        .dark-date-filters .header-date-filters .drp-clear {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-clear:hover {
          background: rgba(255,255,255,0.15) !important;
        }
      `}</style>
      <div className="page-content" style={{ background: 'transparent' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <h2 style={{ fontSize: 18, margin: 0, color:'#fff' }}>Dashboard</h2>
            <div style={{ fontSize: 11, color:'rgba(255,255,255,0.55)', textAlign:'left' }}>
              Overview of tasks, quotes, and field activity
            </div>
          </div>
          <div className="dark-date-filters" style={{ display:'flex', gap:8, alignItems:'center' }}>
            <HeaderDateFilters value={selectedDateRange} onChange={setSelectedDateRange} />
          </div>
        </div>

        {(companyStatsError || quotesError || salesError) ? (
          <div style={{
            background: 'rgba(197, 120, 120, 0.15)',
            border: '1px solid rgba(197, 120, 120, 0.3)',
            borderRadius: 8,
            padding: '10px 14px',
            color: matte.red,
            fontSize: 12,
            marginBottom: 12
          }}>
            {companyStatsError || quotesError || salesError}
          </div>
        ) : null}

        {/* Top KPIs (quick scan) */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <Card
            title="Tasks"
            subtitle="Real-time + due health"
            actions={
              <button className="btn btn-secondary" onClick={() => navigate('/tasks')} style={{ fontSize: 12, height: 28, lineHeight: '26px' }}>
                Open tasks
              </button>
            }
          >
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 8 }}>
              <StatItem
                icon={<FiClock size={16} />}
                label="Due today"
                value={kpis.dueToday}
                color={matte.purple}
                loading={tasksLoading}
                subtitle="Open tasks due by end of day"
                onClick={() => navigate('/tasks?date=today&status=pending,in-progress,incoming')}
              />
              <StatItem
                icon={<FiAlertTriangle size={16} />}
                label="Overdue"
                value={kpis.overdue}
                color={matte.red}
                loading={tasksLoading || companyStatsLoading}
                subtitle="Past due and open"
                onClick={() => navigate('/tasks?status=pending,in-progress,incoming&due=overdue')}
              />
            </div>

            {/* Status distribution */}
            <div style={{ marginTop: 12, borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
              <div style={{ fontSize: 11, color:'rgba(255,255,255,0.6)', marginBottom: 8, textAlign:'left' }}>Status distribution</div>
              <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
                {statusDist.entries.map(([k, v]) => {
                  const color = getTaskStatusColor(k);
                  const widthPct = Math.round((v / statusDist.maxV) * 100);
                  return (
                    <div key={k} style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width: 92, fontSize: 11, color:'rgba(255,255,255,0.65)', textTransform:'capitalize', textAlign:'left' }}>
                        {k.replace('-', ' ')}
                      </div>
                      <div style={{ flex:1, background:'rgba(255,255,255,0.10)', height: 10, borderRadius: 9999, overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ width: `${widthPct}%`, height: 10, background: color }} />
                      </div>
                      <div style={{ width: 32, textAlign:'right', fontSize: 11, color:'#fff', fontWeight: 600 }}>{v}</div>
                    </div>
                  );
                })}
              </div>
              {(tasksLoading || tasksError) ? (
                <div style={{ marginTop: 10, fontSize: 12, color:'rgba(255,255,255,0.55)' }}>
                  {tasksLoading ? 'Loading tasks…' : tasksError}
                </div>
              ) : null}
            </div>

            {/* Geo snapshot (moved here) */}
            <div style={{ marginTop: 10, borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color:'rgba(255,255,255,0.6)', textAlign:'left' }}>Geo snapshot</div>
                <div />
              </div>
              <div style={{ height: 360, borderRadius: 10, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)', background:'#0b1220' }}>
                {(() => {
                  const withLoc = (tasks || []).filter(t => t?.location?.coordinates?.lat && t?.location?.coordinates?.lng);
                  const mapTasks = withLoc.length ? withLoc : tasks;
                  const mapKey = `map-${withLoc.length}-${selectedDateRange?.startDate}-${selectedDateRange?.endDate}`;
                  if (!mapTasks || mapTasks.length === 0) {
                    return (
                      <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.5)', fontSize:12 }}>
                        No tasks to display on the map for this period.
                      </div>
                    );
                  }
                  return (
                    <TaskListMap
                      key={mapKey}
                      tasks={mapTasks}
                      selectedMarker={null}
                      handleTaskClick={() => navigate('/tasks')}
                      setSelectedImage={() => {}}
                    />
                  );
                })()}
              </div>
            </div>
          </Card>

          <Card title="Sales" subtitle="Revenue + pipeline (selected period)" actions={
            <button className="btn btn-secondary" onClick={() => navigate('/quotes')} style={{ fontSize:12, height:28, lineHeight:'26px' }}>
              View all
            </button>
          }>
            {/* Revenue (Collected vs Outstanding) */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap: 10 }}>
              <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color:'rgba(255,255,255,0.6)' }}>Revenue</div>
                  <div style={{ fontSize: 11, color:'rgba(255,255,255,0.45)' }}>{formatINR(totalBilling, 0)}</div>
                </div>
                <SegBar segments={[
                  { value: amountReceived, color: matte.green, title: `Collected: ${formatINR(amountReceived, 0)}`, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) },
                  { value: amountDue, color: matte.red, title: `Outstanding: ${formatINR(amountDue, 0)}`, onClick: () => navigate(`/invoices?status=DUE,OVERDUE&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) }
                ]} />
                <Legend items={[
                  { label: 'Collected', value: formatINR(amountReceived, 0), valueColor: matte.green, color: matte.green, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), title: 'Paid invoices (selected period)' },
                  { label: 'Outstanding', value: formatINR(amountDue, 0), valueColor: matte.red, color: matte.red, onClick: () => navigate(`/invoices?status=DUE,OVERDUE&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), title: 'Due/overdue invoices (selected period)' },
                  { label: 'Collection %', value: `${collectionPct}%`, valueColor: collectionPct >= 80 ? matte.green : (collectionPct >= 50 ? matte.amber : matte.red), color: matte.purple, onClick: null, title: 'Collected ÷ Total billed' },
                  { label: 'Avg ticket', value: avgInvoiceValue != null ? formatINR(avgInvoiceValue, 0) : '-', valueColor: '#fff', color: matte.indigo, onClick: null, title: 'Average value per paid invoice' }
                ]} />
                <div style={{ marginTop: 10, display:'grid', gridTemplateColumns:'1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.75)' }}>
                    <span>POS share</span>
                    <span style={{ color: matte.blue, fontWeight: 700 }}>{posSharePct != null ? `${posSharePct}%` : '-'}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.75)' }}>
                    <span>Avg POS</span>
                    <span style={{ color: matte.teal, fontWeight: 700 }}>{avgPosValue != null ? formatINR(avgPosValue, 0) : '-'}</span>
                  </div>
                </div>
              </div>

              {/* Channel mix */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr', gap: 10 }}>
                <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.6)' }}>Revenue by source</div>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.45)' }}>{formatINR(sourceBreakdown?.total || 0, 0)}</div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'110px 1fr', gap: 12, alignItems:'center' }}>
                    <DonutChart data={allSources.map(s => ({ label: s.label, value: s.amount || 0, color: s.color }))} />
                    <div style={{ display:'grid', gap: 6 }}>
                      {(allSources || []).slice(0, 5).map((s) => (
                        <div key={s.source} style={{ display:'flex', justifyContent:'space-between', gap: 10, fontSize: 12, color:'rgba(255,255,255,0.8)' }}>
                          <span style={{ display:'inline-flex', alignItems:'center', gap: 8, minWidth: 0 }}>
                            <span style={{ width: 10, height: 10, background: s.color, borderRadius: 3, flexShrink: 0 }} />
                            <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.label}</span>
                          </span>
                          <span style={{ color:'#fff', fontWeight: 700 }}>{formatINR(s.amount || 0, 0)}</span>
                        </div>
                      ))}
                      {(allSources || []).length === 0 ? (
                        <div style={{ color:'rgba(255,255,255,0.45)', fontSize: 12 }}>No source data</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color:'rgba(255,255,255,0.6)', textAlign:'left' }}>Quotes pipeline (by status)</div>
                <div style={{ fontSize: 11, color:'rgba(255,255,255,0.45)' }}>{salesLoading || quotesLoading ? 'Loading…' : ''}</div>
              </div>
              {(() => {
                const stages = ['DRAFT', 'SENT', 'ACCEPTED', 'CONVERTED', 'REJECTED', 'CANCELLED'];
                const colors = [matte.allGrey, matte.blue, matte.purple, matte.green, matte.red, matte.rose];
                const values = stages.map(s => Number(quoteSummary.byStatus[s] || 0));
                const total = values.reduce((a, b) => a + b, 0);
                if (!total) {
                  return (
                    <div style={{ color:'rgba(255,255,255,0.45)', fontSize: 12 }}>
                      No quote data for this period.
                    </div>
                  );
                }
                return (
                  <>
                    <SegBar
                      segments={stages.map((s, i) => ({
                        value: values[i],
                        color: colors[i],
                        title: `${s}: ${values[i]}`,
                        onClick: () => navigate(`/quotes?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`)
                      }))}
                    />
                    <Legend
                      items={stages.map((s, i) => ({
                        label: s,
                        value: values[i],
                        color: colors[i],
                        onClick: () => navigate(`/quotes?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`),
                        title: `Quotes in ${s}`
                      }))}
                    />
                    <div style={{ marginTop: 10, display:'grid', gap:6 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize: 12, color:'rgba(255,255,255,0.7)' }}>
                        <span>Total amount</span>
                        <span style={{ color:'#fff', fontWeight: 600 }}>{formatINR(quoteSummary.totalAmount, 0)}</span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize: 12, color:'rgba(255,255,255,0.7)' }}>
                        <span>Converted amount</span>
                        <span style={{ color: matte.green, fontWeight: 700 }}>{formatINR(quoteSummary.convertedAmount, 0)}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </div>

        {/* Row 2: Recent activity */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap: 12, marginTop: 12, alignItems:'stretch' }}>
          <Card
            title="Recent Activity"
            subtitle="Latest tasks + quotes"
            actions={
              <div style={{ display:'inline-flex', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.10)', borderRadius: 9999, padding: 3, gap: 3 }}>
                <button
                  type="button"
                  onClick={() => setActivityFilter('tasks')}
                  style={{
                    fontSize: 12,
                    padding: '5px 12px',
                    borderRadius: 9999,
                    height: 28,
                    background: activityFilter === 'tasks' ? 'rgba(255,255,255,0.14)' : 'transparent',
                    border: 'none',
                    color: activityFilter === 'tasks' ? '#fff' : 'rgba(255,255,255,0.55)',
                    cursor: 'pointer'
                  }}
                >
                  Tasks
                </button>
                <button
                  type="button"
                  onClick={() => setActivityFilter('quotes')}
                  style={{
                    fontSize: 12,
                    padding: '5px 12px',
                    borderRadius: 9999,
                    height: 28,
                    background: activityFilter === 'quotes' ? 'rgba(255,255,255,0.14)' : 'transparent',
                    border: 'none',
                    color: activityFilter === 'quotes' ? '#fff' : 'rgba(255,255,255,0.55)',
                    cursor: 'pointer'
                  }}
                >
                  Quotes
                </button>
              </div>
            }
          >
            <div style={{ display:'flex', flexDirection:'column', gap: 8, maxHeight: 360 }}>
              {activityPageItems.map((a, idx) => (
                <div
                  key={idx}
                  style={{
                    display:'flex',
                    justifyContent:'space-between',
                    alignItems:'center',
                    gap: 10,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 10,
                    padding: '8px 10px'
                  }}
                >
                  <div style={{ display:'flex', alignItems:'center', gap: 10, minWidth: 0 }}>
                    <span style={{ width:8, height:8, borderRadius:9999, background: a.type === 'task' ? matte.allGrey : matte.teal, display:'inline-block', flexShrink:0 }} />
                    <div style={{ display:'flex', flexDirection:'column', minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color:'#fff', textAlign:'left', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {a.title}
                      </div>
                      <div style={{ fontSize: 11, color:'rgba(255,255,255,0.55)', textAlign:'left' }}>
                        {a.subtitle ? `${a.subtitle} • ` : ''}{a.ts.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap: 10, flexShrink:0 }}>
                    {a.type === 'quote' && typeof a.amount === 'number' ? (
                      <div style={{ fontSize: 12, color:'#fff', fontWeight: 600 }}>{formatINR(a.amount, 0)}</div>
                    ) : null}
                    <div style={{ fontSize: 10, fontWeight: 700, background:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.75)', padding:'3px 8px', borderRadius: 9999, border:'1px solid rgba(255,255,255,0.10)' }}>
                      {String(a.status || '').toUpperCase()}
                    </div>
                  </div>
                </div>
              ))}
              {(filteredActivity.length === 0) ? (
                <div style={{ padding: 10, fontSize: 12, color:'rgba(255,255,255,0.5)' }}>
                  No recent activity in this period.
                </div>
              ) : null}
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop: 10 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                disabled={activityPage <= 1}
                style={{ fontSize:12, height:28, lineHeight:'26px' }}
              >
                Prev
              </button>
              <div style={{ fontSize: 12, color:'rgba(255,255,255,0.55)' }}>Page {activityPage} of {activityTotalPages}</div>
              <button
                className="btn btn-secondary"
                onClick={() => setActivityPage(p => Math.min(activityTotalPages, p + 1))}
                disabled={activityPage >= activityTotalPages}
                style={{ fontSize:12, height:28, lineHeight:'26px' }}
              >
                Next
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 